import React, { useState, useEffect, useCallback } from "react";
import Form, { Item, GroupItem } from "devextreme-react/form";
import notify from "devextreme/ui/notify";
import { Button } from "devextreme-react/button";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { getResourceAPI, resetAPI } from "../redux/slices/resourceSlice";
import {
  getVesselSystemDistinct,
  openAuthorizedWindow,
  pendingCall,
} from "../utils/api";
import Loader from "../components/generic/Loader";
import FormStates from "../components/generic/FormStates";
import useResources from "../hooks/useResources";
import useResourceStatuses from "../hooks/useResourceStatus.jsx";
import { tabNames } from "../utils/strings";
import { Grid } from "@material-ui/core";
import { toCustomDate, fromCustomDate } from "../utils/date";
import CustomSelection from "../components/generic/CustomSelection";
import { getUIProperty, setUIProperty } from "../redux/slices/uiSlice";
import qs from "qs";
import { getLocalStorageItem, setLocalStorageItem } from "../utils/general";
import { isEmpty } from "lodash";
import { DateBox } from "devextreme-react";

const createQuery = (formData, disabledByOurRef, disabledByClientRef) => {
  if (disabledByOurRef) return { our_ref: formData.our_ref };
  else if (disabledByClientRef) return { client_ref: formData.client_ref };

  return formData;
};

const Search = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const formStates =
    useSelector((state) => getUIProperty(state, "formStates")) ||
    getLocalStorageItem("formStates");

  const [formData, setFormData] = useState({});

  const [equipments, setEquipments] = useState([]);
  const [makers, setMakers] = useState([]);
  const [serials, setSerials] = useState([]);
  const [types, setTypes] = useState([]);

  const [disabledByClientRef, setDisabledByClientRef] = useState(false);
  const [disabledByOurRef, setDisabledByOurRef] = useState(false);
  const [reportReady, setReportReady] = useState(true);

  const { states, vessels, clients } = useResources();

  const {
    vendorStatus,
    stateStatus,
    clientStatus,
    searchStatus,
    vesselSystemStatus,
  } = useResourceStatuses();

  const handleSearch = useCallback(() => {
    dispatch(setUIProperty({ name: "selectedTradeCaseId", value: null }));
    dispatch(setUIProperty({ name: "filteredItems", value: [] }));
    dispatch(resetAPI());

    dispatch(
      getResourceAPI({
        path: "search",
        query: createQuery(formData, disabledByOurRef, disabledByClientRef),
      })
    ).then(() => history.push(`/admin/results/${tabNames[0]}`));
  }, [disabledByClientRef, disabledByOurRef, dispatch, formData, history]);

  const getReport = useCallback(async () => {
    const { pdfUrl, fileName } = await openAuthorizedWindow(
      `resource/search-report/?${qs.stringify(
        createQuery(formData, disabledByOurRef, disabledByClientRef)
      )}`,
      setReportReady
    );
    dispatch(setUIProperty({ name: "selectedTradeCaseId", value: null }));

    dispatch(setUIProperty({ name: "pdfUrl", value: pdfUrl }));
    dispatch(setUIProperty({ name: "fileName", value: fileName }));
  }, [disabledByClientRef, disabledByOurRef, dispatch, formData]);

  const clearFormData = () => {
    setFormData({});
    notify("Cleared Form", "success", 500);
  };

  const onSaveFormState = (stateName) => {
    const newFormStates = {
      ...formStates,
      [stateName]: formData,
    };

    dispatch(setUIProperty({ name: "formStates", value: newFormStates }));
    setLocalStorageItem("formStates", newFormStates);
  };

  const onClickSaveFormState = () => {
    const stateName = prompt("Give form state name", "");
    if (stateName != null) {
      onSaveFormState(stateName);
    }
  };

  const handleDelete = useCallback(
    (stateName) => {
      const newFormStates = {
        ...formStates,
      };

      delete newFormStates[stateName];

      dispatch(setUIProperty({ name: "formStates", value: newFormStates }));
      setLocalStorageItem("formStates", newFormStates);
    },
    [dispatch, formStates]
  );

  const handleUse = useCallback(
    (stateName) => {
      const stateToUse = formStates[stateName];

      setFormData(stateToUse);
    },
    [formStates]
  );

  // Handle form properties change

  const onClientSelected = useCallback(
    (value) => {
      const foundClient = clients.find((client) => client.id === value);

      setFormData((prevFormData) => ({
        ...prevFormData,
        client: foundClient?.name,
        clientId: value,
      }));

      dispatch(getResourceAPI({ path: "vessel", query: { client_id: value } }));
    },
    [clients, dispatch]
  );

  const onVesselSelected = useCallback(async (value) => {
    const types = await getVesselSystemDistinct({
      vessel_id: value,
      system_type: "type",
    });

    setTypes(types);

    const equipments = await getVesselSystemDistinct({
      vessel_id: value,
      system_type: "equipment",
    });

    setEquipments(equipments);

    const makers = await getVesselSystemDistinct({
      vessel_id: value,
      system_type: "maker",
    });

    setMakers(makers);
    const serials = await getVesselSystemDistinct({
      vessel_id: value,
      system_type: "serial",
    });

    setSerials(serials);
  }, []);

  useEffect(() => {
    pendingCall(stateStatus) && dispatch(getResourceAPI({ path: "state" }));
  }, [dispatch, stateStatus]);

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {(vendorStatus === "pending" ||
        searchStatus === "pending" ||
        !reportReady) && <Loader />}
      <Form
        formData={formData}
        onFieldDataChanged={(e) => {
          const { dataField, value } = e;

          let newValue = value;
          if (dataField === "client_ref")
            setDisabledByClientRef(!!(value && value.length > 0));
          else if (dataField === "our_ref")
            setDisabledByOurRef(!!(value && value.length > 0));
          else if (dataField === "from" || dataField === "to")
            newValue = toCustomDate(value);

          if (!dataField || newValue === undefined) return;

          return setFormData((prevFormData) => ({
            ...prevFormData,
            [dataField]: newValue,
          }));
        }}
        labelLocation="top"
        onContentReady={(e) => e.component.validate()}
        colCount={2}
        id="form"
      >
        <GroupItem caption="General Details" colCount={2}>
          <Item dataField="from">
            <DateBox
              displayFormat="dd/MM/yyyy"
              width="100%"
              onValueChanged={(e) => {
                const { value } = e;
                setFormData((prev) => ({ ...prev, from: toCustomDate(value) }));
              }}
              value={formData.from ? fromCustomDate(formData.from) : null}
            />
          </Item>

          <Item dataField="to">
            <DateBox
              displayFormat="dd/MM/yyyy"
              width="100%"
              onValueChanged={(e) => {
                const { value } = e;
                setFormData((prev) => ({ ...prev, to: toCustomDate(value) }));
              }}
              value={formData.to ? fromCustomDate(formData.to) : null}
            />
          </Item>

          <Item
            dataField="state"
            editorType="dxSelectBox"
            editorOptions={{
              inputAttr: { autocomplete: "state" },
              items: Object.values(states || {}).map((state) => state.state),
              searchEnabled: true,
              disabled:
                stateStatus !== "done" ||
                disabledByClientRef ||
                disabledByOurRef,
            }}
          />
          <Item
            editorOptions={{
              disabled: disabledByClientRef || disabledByOurRef,
            }}
            editorType="dxNumberBox"
            dataField="searchDays"
          />
        </GroupItem>
        <GroupItem caption="Reference" colCount={2}>
          <Item
            dataField="client_ref"
            editorOptions={{
              inputAttr: { autocomplete: "client_ref" },
              disabled: disabledByOurRef,
            }}
          />
          <Item
            dataField="our_ref"
            editorOptions={{
              inputAttr: { autocomplete: "our_ref" },
              disabled: disabledByClientRef,
            }}
          />
        </GroupItem>

        <GroupItem caption="Client Details" colCount={2}>
          <Item dataField="client">
            <CustomSelection
              valueExpr="id"
              value={formData.clientId}
              resourceName="clients"
              resourcePath="client"
              handleChange={(e) => {
                const value = e.value;
                onClientSelected(value);
              }}
            />
          </Item>
          <Item dataField="vessel">
            <CustomSelection
              valueExpr="id"
              displayExpr="name"
              value={formData.vessel}
              dataSource={Object.values(vessels || {})}
              handleChange={(e) => {
                const value = e.value;
                onVesselSelected(value);
                return setFormData((prev) => ({ ...prev, vessel: e.value }));
              }}
              disabled={
                clientStatus !== "done" ||
                disabledByClientRef ||
                disabledByOurRef ||
                !formData.client
              }
            />
          </Item>
          <Item dataField="vendor">
            <CustomSelection
              valueExpr="name"
              displayExpr="name"
              value={formData.vendor}
              resourceName="vendors"
              resourcePath="vendor"
              handleChange={(e) =>
                setFormData((prev) => ({ ...prev, vendor: e.value }))
              }
              disabled={
                !!formData.client &&
                (vendorStatus !== "done" ||
                  disabledByClientRef ||
                  disabledByOurRef)
              }
            />
          </Item>
        </GroupItem>
        <GroupItem caption="Vessel Details" colCount={2}>
          <Item dataField="type">
            <CustomSelection
              valueExpr="description"
              displayExpr="description"
              value={formData.type}
              dataSource={types}
              handleChange={(e) =>
                setFormData((prev) => ({ ...prev, type: e.value }))
              }
              disabled={
                !formData.vessel &&
                (vesselSystemStatus !== "done" ||
                  disabledByClientRef ||
                  disabledByOurRef)
              }
            />
          </Item>

          <Item dataField="equipment">
            <CustomSelection
              valueExpr="description"
              displayExpr="description"
              value={formData.equipment}
              dataSource={equipments}
              handleChange={(e) =>
                setFormData((prev) => ({ ...prev, equipment: e.value }))
              }
              disabled={
                !formData.vessel &&
                (vesselSystemStatus !== "done" ||
                  disabledByClientRef ||
                  disabledByOurRef)
              }
            />
          </Item>

          <Item dataField="maker">
            <CustomSelection
              valueExpr="description"
              displayExpr="description"
              value={formData.maker}
              dataSource={makers}
              handleChange={(e) =>
                setFormData((prev) => ({ ...prev, maker: e.value }))
              }
              disabled={
                !formData.vessel &&
                (vesselSystemStatus !== "done" ||
                  disabledByClientRef ||
                  disabledByOurRef)
              }
            />
          </Item>
          <Item dataField="serial">
            <CustomSelection
              valueExpr="description"
              displayExpr="description"
              value={formData.serial}
              dataSource={serials}
              handleChange={(e) =>
                setFormData((prev) => ({ ...prev, serial: e.value }))
              }
              disabled={
                !formData.vessel &&
                (vesselSystemStatus !== "done" ||
                  disabledByClientRef ||
                  disabledByOurRef)
              }
            />
          </Item>
        </GroupItem>
      </Form>
      <span>
        <Grid
          container
          style={{ marginTop: "16px" }}
          align="center"
          justifyContent="center"
          spacing={5}
        >
          <Grid item xs={3}>
            <Button
              icon="clear"
              type="normal"
              text="Clear"
              hint="Clear Form"
              width="200"
              onClick={clearFormData}
            />
          </Grid>

          <Grid item xs={3}>
            <Button
              icon="save"
              type="normal"
              text="Save Form"
              hint="Save Form State"
              width="200"
              onClick={onClickSaveFormState}
            />
          </Grid>
          <Grid item xs={3}>
            <Button
              icon="doc"
              type="default"
              text="Get Report"
              hint="Generate Report for trade cases"
              width="200"
              onClick={getReport}
            />
          </Grid>
          <Grid item xs={3}>
            <Button
              icon="search"
              type="success"
              text="Search"
              hint="Search trade-cases"
              width="200"
              onClick={handleSearch}
            />
          </Grid>
          {!isEmpty(formStates) && (
            <FormStates
              formStates={formStates}
              handleUse={handleUse}
              handleDelete={handleDelete}
            />
          )}
        </Grid>
      </span>
    </div>
  );
};

export default Search;
